import React from 'react';
import { useOutlet } from '../../context/OutletContext';
import { useOrders } from '../context/OrderContext';
import { TrendingUp, TrendingDown, DollarSign, ShoppingBag, XCircle, Package } from 'lucide-react';
import './OutletComparison.css';

const OutletComparison = () => {
  const { outlets } = useOutlet();
  const { getAllOutletsStats } = useOrders();

  const outletStats = getAllOutletsStats();

  // Calculate totals
  const totals = outletStats.reduce((acc, stat) => ({
    totalOrders: acc.totalOrders + stat.totalOrders,
    totalRevenue: acc.totalRevenue + stat.totalRevenue,
    cancelled: acc.cancelled + stat.cancelled,
  }), { totalOrders: 0, totalRevenue: 0, cancelled: 0 });

  // Find best and worst performing outlets
  const sortedByRevenue = [...outletStats].sort((a, b) => b.totalRevenue - a.totalRevenue);
  const bestOutlet = sortedByRevenue[0];
  const worstOutlet = sortedByRevenue[sortedByRevenue.length - 1];

  return (
    <div className="outlet-comparison">
      <div className="outlet-comparison__header">
        <h2>Outlet Performance Comparison</h2>
        <p>Compare key metrics across all your outlets</p>
      </div>

      {/* Summary Cards */}
      <div className="outlet-comparison__summary">
        <div className="summary-card summary-card--primary">
          <div className="summary-card__icon">
            <ShoppingBag size={24} />
          </div>
          <div className="summary-card__content">
            <span className="summary-card__label">Total Orders</span>
            <span className="summary-card__value">{totals.totalOrders}</span>
          </div>
        </div>

        <div className="summary-card summary-card--success">
          <div className="summary-card__icon">
            <DollarSign size={24} />
          </div>
          <div className="summary-card__content">
            <span className="summary-card__label">Total Revenue</span>
            <span className="summary-card__value">₹{totals.totalRevenue.toFixed(2)}</span>
          </div>
        </div>

        <div className="summary-card summary-card--danger">
          <div className="summary-card__icon">
            <XCircle size={24} />
          </div>
          <div className="summary-card__content">
            <span className="summary-card__label">Cancelled Orders</span>
            <span className="summary-card__value">{totals.cancelled}</span>
          </div>
        </div>

        <div className="summary-card summary-card--info">
          <div className="summary-card__icon">
            <Package size={24} />
          </div>
          <div className="summary-card__content">
            <span className="summary-card__label">Active Outlets</span>
            <span className="summary-card__value">{outlets.length}</span>
          </div>
        </div>
      </div>

      {/* Top/Bottom Performers */}
      {bestOutlet && worstOutlet && outlets.length > 1 && (
        <div className="outlet-comparison__performers">
          <div className="performer-card performer-card--best">
            <div className="performer-card__badge">
              <TrendingUp size={16} />
              <span>Top Performer</span>
            </div>
            <h3>{bestOutlet.outletName}</h3>
            <div className="performer-card__stats">
              <div className="stat">
                <span className="stat__label">Revenue</span>
                <span className="stat__value">₹{bestOutlet.totalRevenue.toFixed(2)}</span>
              </div>
              <div className="stat">
                <span className="stat__label">Orders</span>
                <span className="stat__value">{bestOutlet.totalOrders}</span>
              </div>
            </div>
          </div>

          <div className="performer-card performer-card--worst">
            <div className="performer-card__badge">
              <TrendingDown size={16} />
              <span>Needs Attention</span>
            </div>
            <h3>{worstOutlet.outletName}</h3>
            <div className="performer-card__stats">
              <div className="stat">
                <span className="stat__label">Revenue</span>
                <span className="stat__value">₹{worstOutlet.totalRevenue.toFixed(2)}</span>
              </div>
              <div className="stat">
                <span className="stat__label">Orders</span>
                <span className="stat__value">{worstOutlet.totalOrders}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Comparison Table */}
      <div className="outlet-comparison__table">
        <table>
          <thead>
            <tr>
              <th>Outlet Name</th>
              <th>Total Orders</th>
              <th>Revenue</th>
              <th>Cancelled</th>
              <th>Avg Order Value</th>
              <th>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {sortedByRevenue.map((stat) => {
              const avgOrderValue = stat.totalOrders > 0
                ? (stat.totalRevenue / stat.totalOrders).toFixed(2)
                : 0;
              const successRate = stat.totalOrders > 0
                ? (((stat.totalOrders - stat.cancelled) / stat.totalOrders) * 100).toFixed(1)
                : 0;

              return (
                <tr key={stat.outletId}>
                  <td className="outlet-name">
                    <span className="outlet-name__text">{stat.outletName}</span>
                  </td>
                  <td className="text-center">{stat.totalOrders}</td>
                  <td className="text-right revenue">₹{stat.totalRevenue.toFixed(2)}</td>
                  <td className="text-center cancelled">{stat.cancelled}</td>
                  <td className="text-right">₹{avgOrderValue}</td>
                  <td className="text-center">
                    <span className={`success-rate ${successRate >= 90 ? 'success-rate--high' : successRate >= 70 ? 'success-rate--medium' : 'success-rate--low'}`}>
                      {successRate}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {outletStats.length === 0 && (
        <div className="outlet-comparison__empty">
          <Package size={48} strokeWidth={1} />
          <p>No outlet data available</p>
          <span>Orders will appear here once placed</span>
        </div>
      )}
    </div>
  );
};

export default OutletComparison;
